import React, { useState } from 'react';
import Navbar from '../../components/layout/Navbar/Navbar';
import Footer from '../../components/layout/Footer/Footer';
import './Legal.css';
import { ChevronDown, FileText } from 'lucide-react';

type SectionContent = {
  label?: string;
  text: string;
};

type TermsSection = {
  id: number;
  title: string;
  content: SectionContent[];
};

const Terms = () => {
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});

  const toggleSection = (id: number) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const sections: TermsSection[] = [
    {
      id: 1,
      title: "Intellectual Property & Trademark",
      content: [
        { text: 'All trademarks, business names, logos, service marks, trade names, restaurant names, menu names, and related intellectual property displayed on the Platform remain the property of their respective owners.' },
        { text: 'The Platform may display restaurant names, business information, contact details, operating hours, menu items, pricing, and related business content obtained from publicly available sources, including publicly listed business profiles, company websites, social media pages, online directories, advertisements, customer-facing menus, and other publicly accessible channels.' },
        { text: 'Such display is solely for identification, marketplace discovery, informational purposes, and customer convenience. Nothing contained on the Platform shall be construed as creating any sponsorship, endorsement, affiliation, agency, joint venture, or official partnership relationship unless expressly stated otherwise in writing.' },
        { text: 'Restaurant owners or authorized representatives may contact the Platform to request updates, corrections, verification, claiming of profiles, or removal of listings, and the Platform shall review such requests within a commercially reasonable timeframe.' },
        { text: 'Users are prohibited from copying, reproducing, distributing, modifying, or commercially exploiting any intellectual property displayed on the Platform without authorization from the respective owner.' }
      ]
    },
    {
      id: 2,
      title: "Marketplace Disclaimer",
      content: [
        { text: 'The Platform operates as a technology-enabled marketplace and logistics facilitation service connecting users with independent restaurants, food vendors, and delivery personnel.' },
        { text: 'Unless expressly stated otherwise, restaurants listed on the Platform are independent businesses and are not employees, agents, subsidiaries, or official partners of the Platform.' },
        { text: 'Certain restaurant information displayed on the Platform may be sourced from publicly available channels, including publicly listed business information, company websites, online directories, publicly available menus, advertisements, and social media pages.' },
        { text: 'Menu availability, pricing, preparation times, food quality, ingredient information, operational hours, and order fulfillment are subject to change and are controlled by the respective restaurant or vendor.' },
        { text: 'While the Platform may make reasonable efforts to maintain accurate and updated information, the Platform does not warrant that all restaurant listings, menus, prices, or related information will always be accurate, complete, or current.' },
        { text: 'By placing an order through the Platform, users acknowledge that order fulfillment may depend on restaurant availability, operational capacity, and third-party factors outside the Platform\'s direct control.' }
      ]
    },
    {
      id: 3,
      title: "Data Privacy & Protection",
      content: [
        { text: 'The Platform is committed to protecting users\' personal data in accordance with applicable Nigerian data protection laws, including the Nigeria Data Protection Act 2023.' },
        { text: 'The Platform may collect and process personal information including names, phone numbers, email addresses, delivery addresses, device information, transaction details, geolocation data, and communications necessary for:' },
        { text: '- account creation;' },
        { text: '- order processing;' },
        { text: '- payment facilitation;' },
        { text: '- customer support;' },
        { text: '- logistics coordination;' },
        { text: '- fraud prevention;' },
        { text: '- service improvement;' },
        { text: '- regulatory compliance.' },
        { text: 'The Platform may also process publicly available business information relating to restaurants and vendors for marketplace listing, informational display, and customer convenience purposes.' },
        { text: 'Personal data may be shared with delivery personnel, payment processors, restaurants, technology vendors, service providers, and regulatory authorities strictly where necessary for service delivery, operational support, fraud prevention, or legal compliance.' },
        { text: 'The Platform shall implement reasonable administrative, technical, and organizational safeguards to protect personal data against unauthorized access, disclosure, alteration, misuse, or destruction.' },
        { text: 'Users may request access, correction, deletion, restriction, or inquiry regarding their personal data by contacting the Platform through its designated privacy communication channels.' },
        { text: 'By accessing or using the Platform, users consent to the collection, use, storage, and processing of their personal data for the purposes outlined herein.' }
      ]
    },
    {
      id: 4,
      title: "Platform Services",
      content: [
        { label: '2.1', text: 'The Platform provides a technology-enabled marketplace and logistics service that allows users to order meals and beverages from restaurants, purchase grocery and non-grocery items from supermarkets and shops, request consumables from local markets, purchase over-the-counter pharmaceutical products and medical consumables from pharmacies, and access other products or services that may be made available on the Platform from time to time ("Services").' },
        { label: '2.2', text: 'The Platform contains listings of restaurants, supermarkets, pharmacies, local market vendors, shops, and other businesses ("Vendors") from which users may request products or services for pickup, purchase, and delivery. Certain Vendor information displayed on the Platform may be obtained from publicly available sources, including publicly listed business profiles, company websites, online directories, advertisements, publicly accessible menus, and social media pages.' },
        { label: '2.3', text: 'Unless expressly stated otherwise, the Platform is not affiliated with, endorsed by, sponsored by, or in partnership with any Vendor listed on the Platform. The Platform may display Vendor names, menus, business information, pricing, operating hours, trademarks, and related information solely for informational, marketplace discovery, ordering facilitation, and customer convenience purposes. Nothing contained on the Platform shall be interpreted as creating any agency, franchise, joint venture, employment, partnership, or representative relationship between the Platform and any Vendor unless expressly agreed in writing.' },
        { label: '2.4', text: 'The Platform operates as an independent technology and logistics intermediary. Upon confirmation of a user\'s order and successful payment where applicable, the Platform may facilitate the purchase of products on behalf of users, coordinate pickup, arrange logistics and delivery services, or enable communication between users, riders, and Vendors.' },
        { label: '2.5', text: 'The Platform has no control over and does not guarantee the availability of Vendors, food quality, ingredient composition, preparation standards, hygiene practices, pricing accuracy, delivery readiness, stock availability, pharmaceutical suitability, event validity, or the accuracy of representations made by Vendors. All representations concerning products and services are made solely by the respective Vendor.' },
        { label: '2.6', text: 'The Platform does not warrant or guarantee that meals, groceries, pharmaceutical products, consumables, or other items will meet user expectations, products will be fit for a particular purpose, products will be free from allergens or contamination, OTC pharmaceutical products are suitable for any medical condition, or Vendor descriptions, images, pricing, and advertisements are accurate, complete, lawful, or up to date. To the fullest extent permitted under applicable law, all implied warranties, representations, and conditions are excluded.' },
        { label: '2.7', text: 'Users acknowledge that all purchases made through the Platform are done at their own discretion and risk. The Platform shall not be liable for Vendor conduct, food poisoning, allergic reactions, incorrect orders, unavailable products, cancelled orders, delayed preparation, inaccurate Vendor information, or any loss arising from reliance on Vendor representations.' },
        { label: '2.8', text: 'Vendor owners or authorized representatives who wish to claim a business listing, request correction of information, update menus, verify ownership, or request removal of listings may contact the Platform through designated support or compliance channels. The Platform reserves the right to review and process such requests within a commercially reasonable timeframe.' }
      ]
    },
    {
      id: 5,
      title: "Access to the Platform & Services",
      content: [
        { label: '3.1', text: 'The Platform does not guarantee that the Services, Platform, mobile application, website, or any related features will always be available, uninterrupted, secure, timely, or error-free. Access to the Platform may occasionally be suspended, restricted, interrupted, or limited due to maintenance, upgrades, system failures, internet disruptions, security incidents, regulatory requirements, third-party service interruptions, or circumstances beyond the Platform\'s reasonable control.' },
        { label: '3.2', text: 'The Platform reserves the right to modify, suspend, discontinue, restrict, or withdraw any part of the Services or Platform at any time without prior notice where reasonably necessary.' },
        { label: '3.3', text: 'The Platform shall not be liable for any loss, damages, missed orders, delayed deliveries, failed transactions, data loss, or business interruption arising from any inability to access or use the Platform.' },
        { label: '3.4', text: 'Users are responsible for ensuring that their devices are compatible with the Platform, their internet connection is adequate, and all account information provided is accurate and current.' },
        { label: '3.5', text: 'The Platform may suspend or terminate access to any user who violates applicable laws, breaches these Terms, engages in fraudulent activity, abuses Vendors or delivery personnel, manipulates transactions, or misuses the Platform in any manner.' }
      ]
    },
    {
      id: 6,
      title: "Limitation of Liability",
      content: [
        { text: 'To the maximum extent permitted under applicable law, the Platform, its owners, directors, employees, affiliates, agents, service providers, and partners shall not be liable for any indirect, incidental, consequential, special, punitive, or exemplary damages arising from or relating to use of the Platform, food quality, food preparation, delayed deliveries, inaccurate restaurant information, order cancellations, restaurant unavailability, third-party conduct, technical interruptions, or unauthorized access to user accounts or data.' },
        { text: 'The Platform acts solely as a technology-enabled marketplace and logistics facilitator and does not manufacture, prepare, package, or directly inspect food products listed by independent restaurants and vendors.' },
        { text: 'The Platform shall not be responsible for allergic reactions, food contamination, ingredient omissions, or health-related issues arising from meals prepared by independent vendors.' },
        { text: 'In no event shall the total liability of the Platform exceed the total service fees paid by the affected user in relation to the specific transaction giving rise to the claim.' }
      ]
    },
    {
      id: 7,
      title: "Indemnity",
      content: [
        { text: 'Users, restaurants, vendors, merchants, and delivery personnel agree to indemnify, defend, and hold harmless the Platform, its affiliates, directors, officers, employees, agents, and partners from and against any claims, liabilities, losses, damages, penalties, expenses, or legal costs arising from breach of these Terms, misuse of the Platform, violation of applicable laws, infringement of intellectual property rights, inaccurate business information, food safety issues, fraudulent activity, negligence, or disputes between users and third parties.' },
        { text: 'This indemnity obligation shall survive termination of the user\'s relationship with the Platform.' }
      ]
    },
    {
      id: 8,
      title: "Refund & Cancellation Policy",
      content: [
        { text: 'Orders placed through the Platform may be cancelled subject to restaurant acceptance status, food preparation stage, rider assignment, and operational circumstances.' },
        { text: 'Refunds may be granted in cases including failed payment processing, unavailable restaurants, duplicate transactions, undelivered orders, verified incomplete orders, or other circumstances determined by the Platform in its reasonable discretion.' },
        { text: 'Refund timelines may vary depending on payment providers, financial institutions, and transaction processing channels.' },
        { text: 'The Platform reserves the right to investigate suspicious refund requests and may decline refunds where there is evidence of abuse, fraud, or policy violation.' }
      ]
    },
    {
      id: 9,
      title: "Food Allergy & Health Disclaimer",
      content: [
        { text: 'Customers are responsible for reviewing ingredient information and making independent inquiries regarding allergies, dietary restrictions, or health concerns before placing orders.' },
        { text: 'The Platform does not guarantee that food prepared by independent restaurants or vendors will be free from allergens or cross-contamination risks.' },
        { text: 'Restaurants and vendors remain solely responsible for ingredient disclosure, food preparation practices, and compliance with applicable food safety regulations.' }
      ]
    },
    {
      id: 10,
      title: "Vendor Takedown & Listing Removal",
      content: [
        { text: 'The Platform reserves the right to modify, suspend, restrict, remove, or disable any restaurant, vendor, menu, listing, or business profile at its discretion, including where ownership disputes arise, intellectual property complaints are received, inaccurate information is identified, fraudulent activity is suspected, regulatory concerns arise, or operational risks are detected.' },
        { text: 'Restaurant owners or authorized representatives may request profile corrections, verification, claiming of listings, or removal requests through the Platform\'s designated communication channels.' },
        { text: 'The Platform may request reasonable proof of ownership or authorization before processing such requests.' }
      ]
    },
    {
      id: 11,
      title: "Electronic Communications & Consent",
      content: [
        { text: 'By accessing or using the Platform, users consent to receive electronic communications including notifications, receipts, updates, transactional messages, customer support communications, compliance notices, marketing communications where permitted, and legally required disclosures.' },
        { text: 'Users acknowledge that electronic communications and digital records shall satisfy any legal requirement that such communications be made in writing.' }
      ]
    },
    {
      id: 12,
      title: "Acceptable Use Policy",
      content: [
        { text: 'Users shall not use the Platform for unlawful activities, impersonate any person or business, submit false orders, interfere with Platform operations, attempt unauthorized access to systems, distribute malware or harmful code, engage in fraudulent transactions, harass delivery personnel or vendors, or misuse restaurant information displayed on the Platform.' },
        { text: 'The Platform reserves the right to suspend, restrict, or terminate accounts involved in prohibited activities without prior notice.' }
      ]
    },
    {
      id: 13,
      title: "Dispute Resolution & Governing Law",
      content: [
        { text: 'These Terms shall be governed and interpreted in accordance with the laws of the Federal Republic of Nigeria.' },
        { text: 'Any dispute arising from or relating to the use of the Platform shall first be resolved through good faith negotiations between the parties.' },
        { text: 'Where amicable resolution fails, the dispute shall be submitted to arbitration in accordance with applicable Nigerian arbitration laws before either party may commence court proceedings, except where injunctive or regulatory relief is required.' },
        { text: 'The courts of competent jurisdiction in Nigeria shall retain supervisory jurisdiction where applicable.' }
      ]
    },
    {
      id: 14,
      title: "Delivery & Logistics Disclaimer",
      content: [
        { text: 'Delivery timelines provided on the Platform are estimates only and may be affected by traffic conditions, weather conditions, rider availability, restaurant preparation delays, security situations, public disruptions, or other circumstances beyond the Platform\'s reasonable control.' },
        { text: 'The Platform does not guarantee uninterrupted, error-free, or immediate delivery services at all times.' }
      ]
    },
    {
      id: 15,
      title: "Rider Conduct & Independent Contractor",
      content: [
        { text: 'Delivery personnel engaged through the Platform operate as independent contractors unless expressly stated otherwise.' },
        { text: 'The Platform may conduct onboarding, verification, or compliance procedures but does not guarantee rider conduct in all circumstances.' },
        { text: 'Users are encouraged to report misconduct, safety concerns, or suspicious activity through the Platform\'s official support channels.' }
      ]
    }
  ];

  return (
    <>
      <Navbar />
      <div className="legal-page">
        <section className="legal-hero">
          <div className="legal-hero-background">
            <div className="gradient-blob blob-1"></div>
            <div className="gradient-blob blob-2"></div>
          </div>

          <div className="legal-hero-content">
            <div className="legal-hero-icon">
              <FileText size={48} />
            </div>
            <h1 className="legal-hero-title">Terms of Use</h1>
            <p className="legal-hero-subtitle">
              Please read these terms carefully before using Squaay services
            </p>
            <p className="legal-update-date">
              Last Updated: <span>February 14, 2025</span>
            </p>
          </div>
        </section>

        <section className="legal-intro">
          <div className="legal-container">
            <div className="intro-content">
              <h2>Welcome to Squaay</h2>
              <p>
                Squaay operates as a technology-enabled marketplace and logistics facilitation platform connecting users with independent
                restaurants, food vendors, supermarkets, shops, pharmacies, local market vendors, delivery personnel, and other businesses.
                These Terms of Use ("Terms") govern your access to and use of our website, services, mobile applications, listings,
                ordering tools, and related features (collectively, the "Platform"). By accessing or using the Platform, you agree to be
                bound by these Terms. If you do not agree with any part of these Terms, you must not use the Platform.
              </p>
            </div>
          </div>
        </section>

        <section className="legal-toc">
          <div className="legal-container">
            <h2>Table of Contents</h2>
            <div className="toc-grid">
              {sections.map((section) => (
                <a key={section.id} href={`#section-${section.id}`} className="toc-item">
                  {section.id}. {section.title}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="legal-content">
          <div className="legal-container">
            {sections.map((section) => (
              <div key={section.id} id={`section-${section.id}`} className="legal-section">
                <button
                  className={`section-header ${expandedSections[section.id] ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="section-title-wrapper">
                    <span className="section-number">{section.id}</span>
                    <h3>{section.title}</h3>
                  </div>
                  <ChevronDown size={20} />
                </button>

                {expandedSections[section.id] && (
                  <div className="section-content">
                    {section.content.map((item, idx) => (
                      <p key={idx} className={item.label ? 'definition-item' : 'content-paragraph'}>
                        {item.label && <strong>{item.label}:</strong>}
                        {item.label && ' '}
                        {item.text}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Terms;
